//
import WebComponent from "fg-frontend/src/webComponents/WebComponent";
import * as CursorSpinner from "fg-frontend/src/components/CursorSpinner";

// The server-side form templates hard-code the names of the reCAPTCHA
// success callbacks in the widget's `data-callback`:
//   - checkbox  -> "writeSuccessfulRecaptchaResponseToInput"
//   - invisible -> "submitFormWithRecaptchaResponse"
// Google resolves these names lazily, at the moment the token is delivered
// (i.e. when the user ticks the box / when execute() resolves), NOT at render
// time. So as long as a matching global exists by the time the user interacts,
// it works regardless of whether api.js (async/defer) rendered the widget
// before or after our component mounted. Rewriting `data-callback` to a unique
// per-instance name instead introduces a race: if api.js renders first, the
// DOM keeps the original name and the token lands nowhere — the captcha then
// only "passes on the second attempt".
//
// To still support multiple widgets on one page we keep the template's names
// and install shared dispatchers that route the token to the correct instance.
let instances = [];
let activeInvisible = null;
let globalsInstalled = false;

function liveInstances(mode) {
	instances = instances.filter(
		(inst) => inst.element && document.contains(inst.element),
	);
	return instances.filter((inst) => inst.props.grMode === mode);
}

function installGlobals() {
	if (globalsInstalled) {
		return;
	}
	globalsInstalled = true;

	// Checkbox: called when the user ticks the box.
	window.writeSuccessfulRecaptchaResponseToInput = (response) => {
		const checkboxes = liveInstances("checkbox");
		if (checkboxes.length <= 1) {
			if (checkboxes[0]) {
				checkboxes[0].handleResponseCheckbox(response);
			}
			return;
		}
		// Several checkbox widgets on the page: route the token to the widget
		// that actually produced it.
		const target =
			checkboxes.find((inst) => {
				try {
					return (
						grecaptcha.getResponse(inst.getWidgetId()) === response
					);
				} catch (e) {
					return false;
				}
			}) || checkboxes[0];
		target.handleResponseCheckbox(response);
	};

	// Invisible: called after grecaptcha.execute() resolves. The instance whose
	// submit button was clicked registered itself as active right before exec.
	window.submitFormWithRecaptchaResponse = (response) => {
		const target = activeInvisible || liveInstances("invisible")[0];
		if (target) {
			target.handleResponse(response);
		}
	};
}

class GoogleRecaptcha extends WebComponent {
	constructor(props) {
		super(props);
		this.isRecaptchaExecuted = false;
		this.handleSubmit = this.handleSubmit.bind(this);
		this.handleResponse = this.handleResponse.bind(this);
	}

	didMount() {
		const { grInput, grForm, grSubmit, grMode } = this.props;

		this.inputElement = document.getElementById(grInput);

		// Resolve the .g-recaptcha container that belongs to THIS instance.
		// For the invisible widget `this.element` is the .g-recaptcha div itself;
		// for the checkbox widget the div is a sibling within the same form.
		this.recaptchaContainer = this.element.classList.contains("g-recaptcha")
			? this.element
			: (this.element.closest("form") || document).querySelector(
					".g-recaptcha",
				);

		instances.push(this);
		installGlobals();

		if (grMode != "checkbox") {
			if (grForm) {
				this.formElement = document.getElementById(grForm);
			} else {
				this.formElement = this.element.closest("form");
			}

			if (this.formElement) {
				if (grSubmit) {
					this.submitElement = document.getElementById(grSubmit);
				} else {
					this.submitElement =
						this.formElement.querySelector('[type="submit"]');
				}
				if (this.submitElement) {
					this.submitElement.addEventListener(
						"click",
						this.handleSubmit,
					);
				}
			}
		}
	}

	getWidgetId() {
		const allContainers = Array.from(
			document.querySelectorAll(".g-recaptcha"),
		);
		if (this.recaptchaContainer) {
			const idx = allContainers.indexOf(this.recaptchaContainer);
			return idx >= 0 ? idx : 0;
		}
		return 0;
	}

	handleSubmit(e) {
		const { grMode, grAction, grSitekey } = this.props;

		if (!this.isRecaptchaExecuted) {
			e.preventDefault();
			e.stopPropagation();
			CursorSpinner.pushToQueue("googleRecaptcha");
			if (grecaptcha) {
				if (grMode == "decisionManager") {
					grecaptcha.ready(() => {
						let params = {};
						if (grAction) {
							params["action"] = grAction;
						}
						grecaptcha
							.execute(grSitekey, params)
							.then(this.handleResponse);
					});
				} else {
					// Invisible: mark this instance as the one whose token the
					// shared callback should route back to.
					activeInvisible = this;
					grecaptcha.execute(this.getWidgetId());
				}
			}
		}
	}

	handleResponse(response) {
		this.isRecaptchaExecuted = true;
		this.inputElement.value = response;
		CursorSpinner.removeFromQueue("googleRecaptcha");
		this.submitElement.click();
	}
	handleResponseCheckbox(response) {
		this.inputElement.value = response;
	}
}

export default GoogleRecaptcha;



// WEBPACK FOOTER //
// ./fe/web-career/src/js/components/GoogleRecaptcha.js